(ns manage.campaign-insights-page.view.core
  (:require ["@dnd-kit/core" :as dnd-core]
            ["@dnd-kit/sortable" :as dnd-sortable]
            ["@dnd-kit/utilities" :as dnd-utilities]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Card$default" :as Card]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/Divider$default" :as Divider]
            ["@mui/material/Grid$default" :as Grid]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/ListItemIcon$default" :as ListItemIcon]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Select$default" :as Select]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.set :as set]
            [common.util :refer [spread-props]]
            [common.widgets.daterange-picker :refer [daterange-picker shortcuts-items]]
            [common.widgets.dropdown :refer [dropdown]]
            [manage.adset.util :as adset-util]
            [manage.campaign-insights-page.db :as form]
            [manage.campaign-insights-page.hooks :as hooks]
            [manage.campaign.hooks :as ch]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [reitit.frontend.easy :as r.easy]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui drag-handle [{:keys [listeners attributes]}]
  ($ Icon (spread-props #js {:sx #js {:pt 0.25
                                      :cursor "grab"}}
                        attributes
                        listeners)
     "drag_indicator"))

(defui card-contents
  [{:keys [id header chart data-fn summary-fn format-summary listeners attributes]
    :or {format-summary identity
         data-fn identity}}]
  (let [data (-> (hooks/use-charts-data)
                 (get id))]
    ($ Card {:sx #js {:height "100%"}}
       ($ Stack
          {:direction "row"
           :alignItems "flex-start"
           :gap 1
           :p 3}
          ($ drag-handle {:listeners listeners
                          :attributes attributes})
          ($ Stack {:flex 1 :spacing 0.5}
             (let [summary (summary-fn data)]
               ($ Typography {:variant "h2"}
                  (format-summary summary)))

             ($  Typography
                 {:variant "caption"
                  :sx #js {:color "text.secondary"}}
                 header)))

       ($ Divider)
       ($ Stack
          {:sx #js {:p 3 :boxSizing "content-box"}}
          ($ chart {:data (data-fn data)})))))

(defui sortable-card [{:keys [item]}]
  (let [item-clj (->clj item)
        ;; Use the id as the sortable ID
        item-id (:id item-clj)
        sortable (dnd-sortable/useSortable #js {:id item-id})
        setNodeRef (.-setNodeRef sortable)
        attributes (.-attributes sortable)
        listeners (.-listeners sortable)
        transform (.-transform sortable)
        transition (.-transition sortable)
        is-dragging (.-isDragging sortable)]
    ($ Grid {:size #js {:xs 6}
             :sx #js {:transform (dnd-utilities/CSS.Transform.toString transform)
                      :transition transition
                      :opacity (if is-dragging 0.8 1)}
             :ref setNodeRef}
       ($ card-contents (merge item-clj
                               {:listeners listeners
                                :attributes attributes})))))

(defui sortable-container [{:keys [items set-charts]}]
  (let [sensors (dnd-core/useSensors
                 (dnd-core/useSensor dnd-core/PointerSensor)
                 (dnd-core/useSensor dnd-core/KeyboardSensor
                                     #js {:coordinateGetter dnd-sortable/sortableKeyboardCoordinates}))
        local-ids (mapv :id items)

        handleDragEnd (uix/use-callback
                       (fn [^js event]
                         (let [active-id (some-> event .-active .-id)
                               over-id (some-> event .-over .-id)]
                           (when (not= active-id over-id)
                             (let [old-index (.indexOf local-ids active-id)
                                   new-index (.indexOf local-ids over-id)
                                   ;; Create new order by moving the item
                                   new-order (let [item (nth items old-index)
                                                   without-item (vec (concat (subvec items 0 old-index)
                                                                             (subvec items (inc old-index))))
                                                   [left right] (split-at new-index without-item)]
                                               (vec (concat left [item] right)))]
                               (set-charts new-order)
                               (rf/dispatch [:page.campaign-insights/save-order new-order])))))
                       [items local-ids set-charts])]
    ($ dnd-core/DndContext
       {:sensors sensors
        :collisionDetection dnd-core/closestCenter
        :onDragEnd handleDragEnd}
       ($ dnd-sortable/SortableContext
          {:items (->js local-ids)
           :strategy dnd-sortable/rectSortingStrategy}
          ($ Grid
             {:container true
              :spacing 3
              :py 2
              :sx #js {:width "100%"}}
             (for [id local-ids]
               (let [item (first (filter #(= (:id %) id) items))]
                 ($ sortable-card {:key id
                                   :item item}))))))))

(defui channel-select []
  (let [channel-options (ch/use-campaign-channel-options)
        {:keys [value on-change] :as control} (form/use-bind-control [:channel])

        selected-channel-adsets (hooks/use-selected-channel-adsets)]
    (uix/use-effect (fn []
                      (rf/dispatch [:form.campaign-insights/on-change [:adset-ids]
                                    (if (seq value)
                                      (mapv :id selected-channel-adsets)
                                      [])]))
                    [value selected-channel-adsets])
    ($ Select
       (merge control
              {:variant "filled"
               :size "small"
               :sx #js {:width "20rem"}
               :displayEmpty true
               :renderValue (fn [value]
                              (let [{:keys [label]} (first (filter #(= (:value %) value) channel-options))]
                                ($ :<>
                                   ($ Typography {:component "span"
                                                  :fontWeight 500} "Channel: ")
                                   (or label "All"))))
               :on-change (fn [e]
                            (let [channel (.. e -target -value)]
                              (on-change channel)))})
       ($ MenuItem {:value ""}
          ($ :em "All"))
       (for [{:keys [label value]} channel-options]
         ($ MenuItem
            {:key value
             :value value}
            ($ ListItemIcon
               ($ adset-util/channel-icon {:channel value}))
            label)))))

(defui adset-select []
  (let [adset-options (hooks/use-adset-options)
        {:keys [value] :as control} (form/use-bind-control [:adset-ids])]
    (uix/use-effect (fn []
                      (rf/dispatch [:page.campaign-insights/get-graph-data])
                      (when (empty? value)
                        (rf/dispatch [:form.campaign-insights/on-change [:channel] ""])))
                    [value])
    (r/as-element
     [dropdown (merge control
                      {:size "small"
                       :sx {:width "30rem"
                            ".MuiAutocomplete-tag" {:max-width "calc((100% - 100px) / 2)"}
                            ".MuiOutlinedInput-root" {:padding-right "50px !important"}}
                       :placeholder "Adset(s)"
                       :limit-tags 2
                       :disable-close-on-select true
                       :render-checkboxes? true
                       :options adset-options
                       :multi? true
                       :clearable? true})])))

(defui toolbar [{:keys [has-charts?]}]
  (let [url-context (use-subscribe [:url-context])]
    ($ Stack
       {:direction "row" :gap 1 :py 1}
       ($ Select
          (merge (form/use-bind-control [:aggregate-by])
                 {:variant "filled"
                  :size "small"
                  :sx #js {:width "20rem"}
                  :renderValue (fn [value]
                                 ($ :<>
                                    ($ Typography {:component "span"
                                                   :fontWeight 500} "Aggregate by: ")
                                    value))
                  :on-change (fn [e]
                               (rf/dispatch [:form.campaign-insights/on-change [:aggregate-by] (keyword (.. e -target -value))]))})
          (for [{:keys [label value]} form/aggregrate-options]
            ($ MenuItem
               {:key value
                :value value}
               label)))

       ($ channel-select)
       ($ adset-select)

       ($ daterange-picker
          (merge (-> (form/use-bind-control [:date-range])
                     (set/rename-keys {:on-change :onChange
                                       :on-blur   :onBlur
                                       :on-focus  :onFocus}))
                 {:onAccept #(rf/dispatch [:page.campaign-insights/get-graph-data])
                  :shortcuts (remove (comp #{"lifetime"} :id) (shortcuts-items))
                  :sx #js {:width "23rem"}}))

       ($ Button
          {:href (r.easy/href :advertiser/reports url-context {:new-report true})
           :disabled (not has-charts?)
           :variant "contained"
           :sx #js {:marginLeft "auto"}}
          "Generate report"))))

(defui error-view [{:keys [icon title message action]}]
  ($ Stack
     {:justifyContent "center"
      :alignItems "center"
      :spacing 2
      :sx #js {:flex 1}}
     ($ Icon
        {:fontSize "extraLarge"
         :color "default"}
        icon)
     ($ Typography {:variant "h2"}
        title)
     ($ Typography
        {:textAlign "center"
         :maxWidth "400px"}
        message)
     (when action
       action)))

(defui campaign-insights* [{{:keys [adset-ids]} :form}]
  (let [url-context (use-subscribe [:url-context])
        state (use-subscribe [:resource.campaign-insights/state])
        {:keys [charts set-charts]} (hooks/use-ordered-charts)]
    ($ :<>
       ($ toolbar {:has-charts? (and (= state :success)
                                     (seq charts))})

       ($ Stack
          {:alignItems "center"
           :flex 1}
          (cond
            (= state :pending) ($ Stack
                                  {:justifyContent "center"
                                   :sx #js {:flex 1}}
                                  ($ CircularProgress))
            (= state :failure) ($ error-view
                                  {:icon "close"
                                   :title "Something went wrong."
                                   :message "Please try again. If you continue to experience issues,
                                                     don't hesitate to reach out to our support team for assistance."
                                   :action ($ Button
                                              {:onClick #(rf/dispatch [:page.campaign-insights/get-graph-data])
                                               :variant "contained"}
                                              "Try again")})
            (and (= state :success)
                 (empty? charts)) ($ error-view
                                     {:icon "flashlight_off"
                                      :title "No insights available for analysis."
                                      :message (str "Please note that none of the "
                                                    (if (seq adset-ids)
                                                      "selected adsets"
                                                      "adsets in this campaign")
                                                    " have been live in the selected date range. Ensure your
                                                               adsets are active to view relevant campaign insights.")
                                      :action ($ Button
                                                 {:href (r.easy/href :campaign/page url-context)
                                                  :variant "contained"}
                                                 "Go to adsets")})
            (and (= state :success)
                 (seq charts)) ($ sortable-container
                                  {:items charts
                                   :set-charts set-charts}))))))

(defui campaign-insights []
  (uix/use-effect (fn []
                    (rf/dispatch [:form.campaign-insights/init])
                    (rf/dispatch [:page.campaign-insights/get-graph-data])
                    (fn []
                      (rf/dispatch [:form.campaign-insights/destroy])))
                  [])
  (let [form-value (use-subscribe [:form.campaign-insights/value []])]
    (when form-value
      ($ campaign-insights* {:form form-value}))))
